import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { supabasePublic } from "@/integrations/supabase/publicClient";
import { useToast } from "@/hooks/use-toast";
import { getUTCRangeForLocalDate, isGameInLocalDateRange } from "@/lib/timezoneUtils";
import { triggerGoalFeedbackStandalone } from "@/hooks/useGoalFeedback";

export interface GameMinimal {
  id: string;
  fixture_id: number;
  match_date: string;
  status: 'scheduled' | 'live' | 'finished' | 'postponed' | 'canceled';
  status_short?: string;
  home_score?: number;
  away_score?: number;
  elapsed?: number;
  elapsed_extra?: number;
  home_team: string;
  away_team: string;
  home_team_id?: string;
  away_team_id?: string;
  home_team_logo?: string;
  away_team_logo?: string;
  home_team_api_id?: number;
  away_team_api_id?: number;
  league: string;
  league_api_id: number;
  league_logo_url?: string;
  home_odds?: number;
  draw_odds?: number;
  away_odds?: number;
  was_interrupted?: boolean;
  interrupted_at_minute?: number | null;
  interrupted_score?: string | null;
  // Weather cache
  weather_temp?: number | null;
  weather_description?: string | null;
  weather_humidity?: number | null;
  weather_wind?: number | null;
  weather_icon?: string | null;
  // Red cards from game_statistics
  home_red_cards: number;
  away_red_cards: number;
  // Goal animation tracking
  homeScoreChangedAt?: number;
  awayScoreChangedAt?: number;
}

interface UseGamesListOptions {
  leagueId?: number;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  limit?: number;
  favoriteFixtureIds?: number[];
}

export function useGamesList(options: UseGamesListOptions = {}) {
  const [games, setGames] = useState<GameMinimal[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [internalOffset, setInternalOffset] = useState(0);
  const { toast } = useToast();
  
  // Refs for request management and caching
  const abortControllerRef = useRef<AbortController | null>(null);
  const leagueCacheRef = useRef<Map<number, string>>(new Map());
  const isFetchingRef = useRef(false);
  const internalOffsetRef = useRef(0);

  const {
    leagueId,
    dateFrom,
    dateTo,
    status,
    limit = 20,
    favoriteFixtureIds
  } = options;

  // Sync internalOffsetRef with internalOffset state
  useEffect(() => {
    internalOffsetRef.current = internalOffset;
  }, [internalOffset]);

  // Use public client for odds to avoid token expiry issues on iOS PWA
  const fetchOddsForGames = async (fixtureIds: number[]) => {
    if (fixtureIds.length === 0) return [];
    
    const { data, error } = await supabasePublic
      .from('odds')
      .select('fixture_id, home_odds, draw_odds, away_odds')
      .in('fixture_id', fixtureIds);
    
    if (error) {
      console.error('[useGamesList] Error fetching odds:', error.message);
      return [];
    }
    
    return data || [];
  };

  const fetchGames = useCallback(async () => {
    // 🔒 Prevent simultaneous calls
    if (isFetchingRef.current) {
      console.log('⏭️ Skipping fetch - already in progress');
      return;
    }
    
    isFetchingRef.current = true;
    
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    try {
      setLoading(true);
      
      // Fetch with intelligent batch size to account for local filtering
      const batchMultiplier = (dateFrom && dateTo) ? 3 : 1;
      const batchSize = limit * batchMultiplier;
      
      let query = supabase
        .from('games')
        .select(`
          id,
          fixture_id,
          match_date,
          status,
          status_short,
          home_score,
          away_score,
          elapsed,
          elapsed_extra,
          was_interrupted,
          interrupted_at_minute,
          interrupted_score,
          weather_cache,
          home_team_id,
          away_team_id,
          home_team:teams!games_home_team_id_fkey(id, name, logo_url, api_football_id),
          away_team:teams!games_away_team_id_fkey(id, name, logo_url, api_football_id),
          league:leagues!games_league_id_fkey(name, api_football_id, logo_url),
          game_statistics!left(fixture_id, home_total_shots, away_total_shots, home_ball_possession, home_red_cards, away_red_cards)
        `, { count: 'exact' })
        .order('match_date', { ascending: true })
        .range(internalOffsetRef.current, internalOffsetRef.current + batchSize - 1)
        .abortSignal(controller.signal);

      // Apply filters with league cache
      if (leagueId) {
        // Check cache first
        let internalLeagueId = leagueCacheRef.current.get(leagueId);
        
        if (!internalLeagueId) {
          // Cache miss - fetch from database
          const { data: leagueData } = await supabase
            .from('leagues')
            .select('id')
            .eq('api_football_id', leagueId)
            .abortSignal(controller.signal)
            .single();
          
          // Check if request was cancelled
          if (controller.signal.aborted) {
            isFetchingRef.current = false;
            return;
          }
          
          if (leagueData) {
            internalLeagueId = leagueData.id;
            // Store in cache for future use
            leagueCacheRef.current.set(leagueId, internalLeagueId);
          } else {
            // If league not found, return empty results
            setGames([]);
            setTotal(0);
            setHasMore(false);
            setLoading(false);
            isFetchingRef.current = false;
            return;
          }
        }
        
        query = query.eq('league_id', internalLeagueId);
      }

      if (status && ['scheduled', 'live', 'finished', 'postponed', 'canceled'].includes(status)) {
        // ✅ RIGOROUS LIVE FILTER: status_short + temporal window
        if (status === 'live') {
          const now = new Date();
          const threeHoursAgo = new Date(now.getTime() - 3 * 60 * 60 * 1000).toISOString();
          const oneHourAhead = new Date(now.getTime() + 60 * 60 * 1000).toISOString();
          
          query = query
            .eq('status', 'live')
            .in('status_short', ['1H', '2H', 'HT', 'ET', 'P', 'LIVE', 'SUSP', 'INT'])
            .gte('match_date', threeHoursAgo)
            .lte('match_date', oneHourAhead);
        } else {
          query = query.eq('status', status as 'scheduled' | 'live' | 'finished' | 'postponed' | 'canceled');
        }
      }

      // Filter by favorite fixture IDs if provided
      if (favoriteFixtureIds && favoriteFixtureIds.length > 0) {
        query = query.in('fixture_id', favoriteFixtureIds);
      }

      if (dateFrom && dateTo) {
        // Expand UTC range to capture all games that could appear on the local date
        // This handles timezone differences (e.g., a game at 23:30 UTC on day 5 
        // appears on day 6 in Portugal UTC+1)
        const { utcFrom, utcTo } = getUTCRangeForLocalDate(dateFrom, dateTo);
        query = query.gte('match_date', utcFrom).lt('match_date', utcTo);
      }

      const { data, error, count } = await query;
      
      // Check if request was cancelled
      if (controller.signal.aborted) {
        isFetchingRef.current = false;
        return;
      }

      if (error) {
        // Ignore abort errors (request was intentionally cancelled)
        if (error.message === 'AbortError' || error.code === 'PGRST301') {
          isFetchingRef.current = false;
          return;
        }
        
        console.error('Error fetching games:', error);
        toast({
          title: "Erro",
          description: "Erro ao carregar jogos",
          variant: "destructive"
        });
        isFetchingRef.current = false;
        return;
      }

      // Fetch odds separately for the retrieved games
      const fixtureIds = (data || []).map((game: any) => game.fixture_id);
      const oddsData = await fetchOddsForGames(fixtureIds);
      
      // Create a map of odds indexed by fixture_id
      const oddsMap = new Map(
        oddsData.map(odd => [odd.fixture_id, odd])
      );

      // Transform the data to match GameMinimal interface
      const transformedData = (data || [])
        .map((game: any) => {
          const odds = oddsMap.get(game.fixture_id);
          
          // Detect if game was interrupted: scheduled + future + has real live activity
          const isFutureScheduled = game.status === 'scheduled' && new Date(game.match_date) > new Date();

          // Supabase LEFT JOIN returns an array for game_statistics; take first element if present
          const statsArray = game.game_statistics;
          const stats = Array.isArray(statsArray) && statsArray.length > 0 ? statsArray[0] : null;

          const hasLiveActivity = !!stats && (
            (stats.home_total_shots ?? 0) > 0 ||
            (stats.away_total_shots ?? 0) > 0 ||
            (stats.home_ball_possession !== null && stats.home_ball_possession !== undefined)
          );

          const wasInterruptedComputed = isFutureScheduled && hasLiveActivity;
          
          return {
            id: game.id,
            fixture_id: game.fixture_id,
            match_date: game.match_date,
            status: game.status,
            status_short: game.status_short,
            home_score: game.home_score,
            away_score: game.away_score,
            elapsed: game.elapsed,
            elapsed_extra: game.elapsed_extra,
            home_team: game.home_team?.name || 'Unknown',
            away_team: game.away_team?.name || 'Unknown',
            home_team_id: game.home_team_id,
            away_team_id: game.away_team_id,
            home_team_logo: game.home_team?.logo_url,
            away_team_logo: game.away_team?.logo_url,
            home_team_api_id: game.home_team?.api_football_id,
            away_team_api_id: game.away_team?.api_football_id,
            league: game.league?.name || 'Unknown',
            league_api_id: game.league?.api_football_id || 0,
            league_logo_url: game.league?.logo_url,
            home_odds: odds?.home_odds,
            draw_odds: odds?.draw_odds,
            away_odds: odds?.away_odds,
            was_interrupted: game.was_interrupted ?? wasInterruptedComputed,
            interrupted_at_minute: game.interrupted_at_minute,
            interrupted_score: game.interrupted_score,
            weather_temp: game.weather_cache?.temp_c ?? null,
            weather_description: game.weather_cache?.description ?? null,
            weather_humidity: game.weather_cache?.humidity ? parseInt(game.weather_cache.humidity) : null,
            weather_wind: game.weather_cache?.wind_speed ?? null,
            weather_icon: game.weather_cache?.icon ?? null,
            home_red_cards: stats?.home_red_cards ?? 0,
            away_red_cards: stats?.away_red_cards ?? 0,
          };
        })
        .filter((game) => {
          // Post-fetch filter: only keep games that fall within the LOCAL date range
          // This ensures exact local date matching after the expanded UTC query
          if (dateFrom && dateTo) {
            return isGameInLocalDateRange(game.match_date, dateFrom, dateTo);
          }
          return true;
        });

      setGames(internalOffsetRef.current === 0 ? transformedData : prev => [...prev, ...transformedData]);
      setTotal(count || 0);
      
      // hasMore = true if:
      // 1. Query returned full batch (may have more data)
      // 2. OR haven't shown all games from total count yet
      const queryReturnedFullBatch = (data?.length || 0) >= batchSize;
      const moreGamesInDatabase = (internalOffsetRef.current + batchSize) < (count || 0);
      setHasMore(queryReturnedFullBatch || moreGamesInDatabase);
    } catch (error: any) {
      // Ignore abort errors (request was cancelled)
      if (error.name === 'AbortError' || controller.signal.aborted) {
        isFetchingRef.current = false;
        return;
      }
      
      console.error('Error in fetchGames:', error);
      toast({
        title: "Erro",
        description: "Erro inesperado ao carregar jogos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
      isFetchingRef.current = false;
    }
  }, [leagueId, dateFrom, dateTo, status, limit, favoriteFixtureIds, toast]);

  // Consolidated effect: clear games + fetch with debounce
  useEffect(() => {
    // Clear games immediately
    setGames([]);
    setTotal(0);
    setHasMore(false);
    setInternalOffset(0);
    
    // Small delay to prevent excessive calls when filters change rapidly
    const timer = setTimeout(() => {
      fetchGames();
    }, 100);
    
    return () => {
      clearTimeout(timer);
      // Cancel any pending request when filters change
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [leagueId, dateFrom, dateTo, status, limit, favoriteFixtureIds?.join(','), fetchGames]);

  // Separate effect for pagination: fetch more games when offset changes
  useEffect(() => {
    // Skip initial render (offset = 0 is handled by main effect)
    if (internalOffset === 0) return;
    
    console.log(`📄 Loading more games (offset: ${internalOffset})`);
    fetchGames();
  }, [internalOffset, fetchGames]);

  // 📡 REALTIME: Listen for game score updates directly
  useEffect(() => {
    const channel = supabase
      .channel('games-list-realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'games',
        },
        (payload) => {
          const updatedGame = payload.new as any;
          
          // Update the specific game in state if it exists
          const now = Date.now();
          setGames((prevGames) => 
            prevGames.map((game) => {
              if (game.fixture_id === updatedGame.fixture_id) {
                // Detect if scores changed (goal scored)
                const homeScored = updatedGame.home_score !== null && 
                  updatedGame.home_score > (game.home_score ?? 0);
                const awayScored = updatedGame.away_score !== null && 
                  updatedGame.away_score > (game.away_score ?? 0);
                
                if (homeScored || awayScored) {
                  console.log(`⚽ GOAL! ${game.home_team} ${updatedGame.home_score}-${updatedGame.away_score} ${game.away_team}`);
                  // Trigger sound and vibration feedback
                  triggerGoalFeedbackStandalone();
                } else {
                  console.log(`🔄 Realtime update: ${game.home_team} ${updatedGame.home_score}-${updatedGame.away_score} ${game.away_team}`);
                }
                
                return {
                  ...game,
                  home_score: updatedGame.home_score,
                  away_score: updatedGame.away_score,
                  status: updatedGame.status,
                  status_short: updatedGame.status_short,
                  elapsed: updatedGame.elapsed,
                  elapsed_extra: updatedGame.elapsed_extra,
                  // Track when scores changed for animation
                  homeScoreChangedAt: homeScored ? now : game.homeScoreChangedAt,
                  awayScoreChangedAt: awayScored ? now : game.awayScoreChangedAt,
                };
              }
              return game;
            })
          );
        }
      )
      // 🟥 REALTIME: Listen for game_statistics updates (red cards)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_statistics',
        },
        (payload) => {
          const stats = payload.new as any;
          if (!stats?.fixture_id) return;
          
          setGames((prevGames) =>
            prevGames.map((game) => {
              if (game.fixture_id === stats.fixture_id) {
                const newHomeRed = stats.home_red_cards ?? 0;
                const newAwayRed = stats.away_red_cards ?? 0;
                if (newHomeRed !== game.home_red_cards || newAwayRed !== game.away_red_cards) {
                  console.log(`🟥 Red cards update: ${game.home_team} (${newHomeRed}) vs ${game.away_team} (${newAwayRed})`);
                  return { ...game, home_red_cards: newHomeRed, away_red_cards: newAwayRed };
                }
              }
              return game;
            })
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);


  const loadMore = () => {
    if (!loading && hasMore) {
      const batchSize = (dateFrom && dateTo) ? limit * 3 : limit;
      setInternalOffset(prev => prev + batchSize);
    }
  };

  const refresh = () => {
    // Reset pagination state BEFORE fetching
    setInternalOffset(0);
    internalOffsetRef.current = 0;
    
    // Clear existing games
    setGames([]);
    setTotal(0);
    setHasMore(false);
    
    // Fetch fresh data with offset=0 (maintains live updates)
    fetchGames();
  };

  return {
    games,
    loading,
    hasMore,
    total,
    loadMore,
    refresh,
    refetch: fetchGames
  };
}
